import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { MessageCircle, Mail, Phone, MapPin, ShieldCheck, BadgeCheck, Lock } from 'lucide-react';
import { fadeInUp } from '../../lib/motion';
import { COOKIE_PREFS_EVENT } from '../../lib/cookieConsent';

interface NavigationLink {
  name: string;
  onClick: () => void;
}

interface LegalLink {
  name: string;
  path: string;
}

interface TrustBadge {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate(`/#${sectionId}`);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const navigationLinks: NavigationLink[] = [
    { name: 'Home', onClick: () => scrollToSection('home') },
    { name: 'Funzionalità', onClick: () => scrollToSection('features') },
    { name: "Casi d'uso", onClick: () => scrollToSection('settori') },
    { name: 'FAQ', onClick: () => scrollToSection('faq') }
  ];

  const legalLinks: LegalLink[] = [
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Cookie Policy', path: '/cookie-policy' },
    { name: 'Termini di Servizio', path: '/termini' },
    { name: 'Eliminazione Dati', path: '/eliminazione-dati' }
  ];

  const trustBadges: TrustBadge[] = [
    { icon: ShieldCheck, label: 'GDPR' },
    { icon: BadgeCheck, label: 'Dati in UE' },
    { icon: Lock, label: 'WhatsApp API' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
          <motion.div {...fadeInUp}>
            <div className="flex items-center space-x-2 mb-6">
              <div className="relative">
                <MessageCircle className="h-8 w-8 text-primary" aria-hidden="true" />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-success rounded-full"></div>
              </div>
              <span className="text-2xl font-heading font-bold">
                Chat<span className="text-primary">Ly</span>
              </span>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              La piattaforma leader per l'automazione delle conversazioni WhatsApp.
              Trasforma il tuo customer service con l'intelligenza artificiale.
            </p>

            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-primary" aria-hidden="true" />
                <a href="mailto:info@chatly.it" className="text-gray-300 hover:text-white transition-colors">
                  info@chatly.it
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-primary" aria-hidden="true" />
                <a href="tel:+393937169038" className="text-gray-300 hover:text-white transition-colors">
                  +39 393 716 9038
                </a>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-primary mt-0.5" aria-hidden="true" />
                <span className="text-gray-300">
                  Via di sant alessio 39<br />
                  51100 Pistoia, Italia
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div
            {...fadeInUp}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h3 className="text-lg font-bold mb-6">Navigazione</h3>
            <ul className="space-y-3">
              {navigationLinks.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={link.onClick}
                    className="text-gray-300 hover:text-white transition-colors cursor-pointer"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            {...fadeInUp}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="text-lg font-bold mb-6">Legale</h3>
            <ul className="space-y-3">
              {legalLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent(COOKIE_PREFS_EVENT))}
                  className="text-gray-300 hover:text-white transition-colors cursor-pointer"
                >
                  Preferenze cookie
                </button>
              </li>
            </ul>
          </motion.div>
        </div>
      </div>

      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row justify-between items-center gap-4"
          >
            <div className="text-gray-400 text-sm text-center md:text-left">
              © 2026 ChatLy.it — Tutti i diritti riservati | P.IVA 02138520479
              <span className="mx-2 hidden sm:inline">·</span>
              <br className="sm:hidden" />
              Powered by{' '}
              <a
                href="https://deltacode.it"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-gray-200 hover:text-primary transition-colors"
              >
                DeltaCode
              </a>
            </div>

            <div className="flex items-center gap-4 text-xs text-gray-400">
              {trustBadges.map((badge, index) => (
                <div key={index} className="flex items-center gap-1.5">
                  <badge.icon className="h-4 w-4 text-primary" aria-hidden="true" />
                  {badge.label}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
